import { maxFormColumns, uid, type Field, type Project } from "@proof/core";

type Group = Project["groups"][number];
export const layoutColumns: readonly number[] = Array.from(
  { length: maxFormColumns },
  (_, index) => index + 1,
);
/** Where a field can be placed in the form layout. */
export type FieldPlacement =
  | { kind: "section"; groupId: string; index: number }
  | { kind: "ungrouped"; index: number }
  | { kind: "removed" };

const byOrder = <T extends { order: number }>(a: T, b: T) => a.order - b.order;
const onForm = (field: Field) => field.formDisplay !== "remove";

export function sectionsInColumn(project: Project, column: number): Group[] {
  return project.groups.filter((g) => g.column === column).sort(byOrder);
}
export function fieldsInSection(project: Project, groupId: string): Field[] {
  return project.fields
    .filter((f) => f.groupId === groupId && onForm(f))
    .sort(byOrder);
}
/** Fields on the form without a section (or whose section no longer exists). */
export function ungroupedFields(project: Project): Field[] {
  const groups = new Set(project.groups.map((g) => g.id));
  return project.fields
    .filter((f) => (!f.groupId || !groups.has(f.groupId)) && onForm(f))
    .sort(byOrder);
}
export function removedFields(project: Project): Field[] {
  return project.fields.filter((f) => !onForm(f)).sort(byOrder);
}

/**
 * Renumbers section and field order to follow the visual layout: columns left
 * to right, sections top to bottom, then ungrouped and removed fields.
 * `fieldLists`/`sectionLists` pin an explicit sequence for one container.
 */
function renumber(
  project: Project,
  fieldLists: Record<string, Field[]> = {},
  sectionLists: Record<number, Group[]> = {},
): Project {
  const sections = layoutColumns.flatMap(
    (column) => sectionLists[column] || sectionsInColumn(project, column),
  );
  const sectionOrder = new Map(sections.map((g, i) => [g.id, i]));
  const sequence = [
    ...sections.flatMap(
      (g) => fieldLists[g.id] || fieldsInSection(project, g.id),
    ),
    ...(fieldLists[""] || ungroupedFields(project)),
    ...removedFields(project),
  ];
  const fieldOrder = new Map(sequence.map((f, i) => [f.id, i]));
  return {
    ...project,
    groups: project.groups.map((g) => ({
      ...g,
      order: sectionOrder.get(g.id) ?? g.order,
    })),
    fields: project.fields.map((f) => ({
      ...f,
      order: fieldOrder.get(f.id) ?? f.order,
    })),
  };
}

export function moveField(
  project: Project,
  fieldId: string,
  placement: FieldPlacement,
): Project {
  const field = project.fields.find((f) => f.id === fieldId);
  if (!field) return project;
  if (placement.kind === "removed")
    // Removing keeps the section so the control can come back where it was.
    return renumber({
      ...project,
      fields: project.fields.map((f) =>
        f.id === fieldId ? { ...f, formDisplay: "remove" } : f,
      ),
    });
  const groupId = placement.kind === "section" ? placement.groupId : undefined;
  const moved: Field = {
    ...field,
    groupId,
    formDisplay: field.formDisplay === "remove" ? "show" : field.formDisplay,
  };
  const next = {
    ...project,
    fields: project.fields.map((f) => (f.id === fieldId ? moved : f)),
  };
  const list = (
    groupId ? fieldsInSection(next, groupId) : ungroupedFields(next)
  ).filter((f) => f.id !== fieldId);
  const index = Math.max(0, Math.min(placement.index, list.length));
  list.splice(index, 0, moved);
  return renumber(next, { [groupId || ""]: list });
}

export function moveSection(
  project: Project,
  groupId: string,
  column: number,
  index: number,
): Project {
  const group = project.groups.find((g) => g.id === groupId);
  if (!group) return project;
  const moved = { ...group, column };
  const next = {
    ...project,
    groups: project.groups.map((g) => (g.id === groupId ? moved : g)),
  };
  const list = sectionsInColumn(next, column).filter((g) => g.id !== groupId);
  list.splice(Math.max(0, Math.min(index, list.length)), 0, moved);
  return renumber(next, {}, { [column]: list });
}

export function addSection(
  project: Project,
  column = 1,
  name = "New section",
): { project: Project; id: string } {
  const id = uid("group");
  const next = {
    ...project,
    groups: [
      ...project.groups,
      {
        id,
        name,
        column,
        order: project.groups.length,
        visible: true,
        note: "",
      },
    ],
  };
  return {
    id,
    project: renumber(
      next,
      {},
      {
        [column]: [
          ...sectionsInColumn(project, column),
          next.groups[next.groups.length - 1],
        ],
      },
    ),
  };
}

/** Deletes a section; its fields become ungrouped and references are dropped. */
export function deleteSection(project: Project, groupId: string): Project {
  return {
    ...project,
    groups: project.groups.filter((g) => g.id !== groupId),
    fields: project.fields.map((f) =>
      f.groupId === groupId ? { ...f, groupId: undefined } : f,
    ),
    tabs: project.tabs.map((t) => ({
      ...t,
      groupIds: t.groupIds.filter((id) => id !== groupId),
    })),
    triggers: project.triggers.map((t) => ({
      ...t,
      show: t.show.filter((x) => x !== groupId),
      hide: t.hide.filter((x) => x !== groupId),
      clear: t.clear.filter((x) => x !== groupId),
    })),
  };
}

export function addTab(
  project: Project,
  name = "New tab",
): { project: Project; id: string } {
  const id = uid("tab");
  return {
    id,
    project: {
      ...project,
      tabs: [
        ...project.tabs,
        {
          id,
          name,
          order: Math.max(-1, ...project.tabs.map((t) => t.order)) + 1,
          groupIds: [],
        },
      ],
    },
  };
}
export function setSectionInTab(
  project: Project,
  tabId: string,
  groupId: string,
  included: boolean,
): Project {
  return {
    ...project,
    tabs: project.tabs.map((t) =>
      t.id !== tabId
        ? t
        : {
            ...t,
            groupIds: included
              ? [...t.groupIds.filter((id) => id !== groupId), groupId]
              : t.groupIds.filter((id) => id !== groupId),
          },
    ),
  };
}
